import { useCallback, useEffect, useRef, useState } from "react";
import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { clearPromptRetryIdentitiesForComposer } from "@/lib/prompt-receipt-bus";
import { clearPromptRetryEnvelopesForComposer } from "@/lib/prompt-retry-envelope";
import type { InputDestinationKey } from "@/lib/input-destination-key";

const KEY_PREFIX = "denker.input-draft.";
const DEBOUNCE_MS = 250;
const RESTORE_DRAFT_EVENT = "denker:restore-input-draft";

export function destinationDraftComposerId(
  destination: InputDestinationKey,
): string {
  return `destination-draft:${destination}`;
}

/** Destination keys are the same for every account, so the stored draft
 *  carries the signed-in account: one account never sees another's draft. */
function storageKey(destination: InputDestinationKey): string {
  const account = conversationBrowserAccountKey() ?? "signed-out";
  return `${KEY_PREFIX}${account}.${destination}`;
}

function mergeRetryableDraft(current: string, rejectedText: string): string {
  const retry = rejectedText.trim();
  if (!retry || current === retry) return current;
  return current.trim() ? `${current}\n${retry}` : retry;
}

export function restoreDestinationDraft(
  destination: InputDestinationKey,
  rejectedText: string,
): void {
  if (typeof window === "undefined") return;
  const key = storageKey(destination);
  let current = "";
  try {
    current = localStorage.getItem(key) ?? "";
  } catch {
    // The mounted composer can still receive the retryable text via the event.
  }
  const restored = mergeRetryableDraft(current, rejectedText);
  try {
    if (restored) localStorage.setItem(key, restored);
  } catch {
    // Draft persistence is best-effort; never lose the in-memory restoration.
  }
  window.dispatchEvent(
    new CustomEvent(RESTORE_DRAFT_EVENT, {
      detail: { destination, text: rejectedText },
    }),
  );
}

/** The input bar's draft for one destination: switching the viewed project
 *  keeps it, and picking another conversation shows that one's draft. */
export function useDestinationDraft(
  destination: InputDestinationKey,
): [string, (v: string) => void] {
  // Read per render: a switch of account reloads the draft like a new destination.
  const key = storageKey(destination);
  const [value, setValueState] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(key) ?? "";
  });
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ key: string; value: string } | null>(null);
  const valueRef = useRef(value);

  const flush = useCallback(() => {
    if (!pendingRef.current || typeof window === "undefined") return;
    const { key, value: v } = pendingRef.current;
    if (v === "") localStorage.removeItem(key);
    else localStorage.setItem(key, v);
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (writeTimer.current) {
      clearTimeout(writeTimer.current);
      writeTimer.current = null;
      flush();
    }
    if (typeof window === "undefined") return;
    const next = localStorage.getItem(key) ?? "";
    valueRef.current = next;
    setValueState(next);
  }, [key, flush]);

  const setValue = useCallback(
    (v: string) => {
      if (v !== valueRef.current) {
        const composerId = destinationDraftComposerId(destination);
        clearPromptRetryIdentitiesForComposer(composerId);
        clearPromptRetryEnvelopesForComposer(composerId);
      }
      valueRef.current = v;
      setValueState(v);
      if (typeof window === "undefined") return;
      pendingRef.current = { key, value: v };
      if (writeTimer.current) clearTimeout(writeTimer.current);
      writeTimer.current = setTimeout(() => {
        flush();
        writeTimer.current = null;
      }, DEBOUNCE_MS);
    },
    [destination, key, flush],
  );

  useEffect(() => {
    const restore = (event: Event) => {
      const detail = (
        event as CustomEvent<{ destination: string; text: string }>
      ).detail;
      if (detail.destination !== destination) return;
      setValueState((current) => {
        const restored = mergeRetryableDraft(current, detail.text);
        valueRef.current = restored;
        pendingRef.current = { key, value: restored };
        flush();
        return restored;
      });
    };
    window.addEventListener(RESTORE_DRAFT_EVENT, restore);
    return () => window.removeEventListener(RESTORE_DRAFT_EVENT, restore);
  }, [flush, destination, key]);

  return [value, setValue];
}
